import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolStep,
  ProtocolWarning,
} from "../types";

/**
 * Bemfola — the same molecule as Gonal-F, and a pen you throw away.
 *
 * It answered to Gonal-F's protocol until 2026-09-28, which walked a patient
 * through keeping her pen for 28 days. Bemfola's is **single use**: one
 * injection, then the whole pen goes in the sharps container, and the cartridge
 * cannot be removed. ANSM's committee on biosimilar substitution sets out
 * exactly this distinction — multi-dose adjustable, single-use, reusable with
 * cartridges — and records that dose errors "sont déjà constatées actuellement"
 * while permitting substitution at the pharmacy counter mid-protocol. "Keep the
 * pen" is one of the errors they mean.
 *
 * ### Priming, which a single-use pen needs most
 *
 * Gonal-F is primed once and then carried for 28 days. Bemfola is primed every
 * evening, because every evening it is a new pen — so the step Gonal-F's walk
 * can afford to treat as setup is, here, part of each dose.
 *
 * ### The dose is not free
 *
 * Each strength delivers a **restricted list** of doses rather than any
 * multiple of 12.5 IU — the 75 IU pen does 37.5, 50, 62.5 and 75, and the 450
 * does nine values that are not evenly spaced. So the dose step asks her to
 * check the number against the carton, and a dose the pen cannot deliver has a
 * warning of its own: splitting one across two pens is a documented arithmetic
 * error, not a workaround.
 *
 * ### The thigh
 *
 * Its leaflet names the front of the thigh alongside the abdomen. The site is
 * in the step's words rather than in a `sites` array, because the camera guide
 * this catalogue has for a thigh belongs to the walks that declare one — that
 * is a separate decision, not a side effect of adding a brand.
 */

const BRANDS = [
  {
    name: "Bemfola",
    markets: [
      "AT",
      "BE",
      "BG",
      "CY",
      "CZ",
      "DE",
      "DK",
      "EE",
      "ES",
      "FI",
      "FR",
      "GB",
      "GR",
      "HR",
      "HU",
      "IE",
      "IS",
      "IT",
      "LI",
      "LT",
      "LU",
      "LV",
      "MT",
      "NL",
      "NO",
      "PL",
      "PT",
      "RO",
      "SE",
      "SI",
      "SK",
      "AU",
      "NZ",
    ],
  },
] as const satisfies readonly ProtocolBrand[];
/**
 * The same missed-dose sentence follitropin alfa's other brands carry.
 *
 * Identical on purpose: the molecule is the same, her pharmacy may hand her a
 * different brand of it mid-protocol, and a rule that changed with the box
 * would be a rule about the box. No hour is stated, because no gonadotropin
 * label states one — see `docs/features/injection-coach/TECHNIQUE.md`.
 */
const MISSED_DOSE: ProtocolWarning = {
  id: "bemfola.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed or late dose - Never take two doses to catch up. Tell your clinic as soon as you can and follow what they say. If you cannot reach them the same day, take the dose when you remember rather than skipping it, and tell them at your next appointment.",
    fr: "Dose oubliée ou en retard - Ne doublez jamais la dose pour rattraper. Prévenez votre centre dès que possible et suivez ses consignes. Si vous ne pouvez pas les joindre le jour même, faites l'injection quand vous y pensez plutôt que de sauter la dose, et signalez-le à votre prochain rendez-vous.",
  },
  sourceSegment:
    "⚠ Missed or late dose - Never take two doses to catch up. Tell your clinic as soon as you can and follow what they say. If you cannot reach them the same day, take the dose when you remember rather than skipping it, and tell them at your next appointment.",
};
/** OHSS, in the words this molecule's content uses everywhere. */
const WHEN_TO_CALL: ProtocolWarning = {
  id: "bemfola.when-to-call",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "When to call your clinic - Stop injecting and call your clinic for rapid weight gain over 2 lbs/day, severe abdominal pain, nausea or vomiting, difficulty breathing, decreased urination, or injection site infection signs.",
    fr: "Quand contacter votre centre - Arrêtez d'injecter et appelez votre centre en cas de prise de poids rapide de plus de 1 kg/jour, douleurs abdominales sévères, nausées ou vomissements, difficultés respiratoires, diminution de la diurèse ou signes d'infection au point d'injection.",
  },
  sourceSegment:
    "⚠ When to call your clinic - Stop injecting and call your clinic for rapid weight gain over 2 lbs/day, severe abdominal pain, nausea or vomiting, difficulty breathing, decreased urination, or injection site infection signs.",
};
/**
 * The half of "when to call" that does not belong to a clinic.
 *
 * Anaphylaxis is not a phone call to a fertility clinic, and breathlessness
 * appears in both sentences on purpose: respiratory OHSS is days of ascites and
 * goes to the clinician, sudden breathlessness with swelling is minutes and
 * goes to emergency services.
 */
const BREATHING_OR_ALLERGIC_REACTION: ProtocolWarning = {
  id: "bemfola.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Sudden difficulty breathing, swelling of the face or throat, a rash or hives need emergency services straight away.",
    fr: "Aide urgente - Des difficultés respiratoires soudaines, un gonflement du visage ou de la gorge, une éruption cutanée ou de l'urticaire nécessitent d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Sudden difficulty breathing, swelling of the face or throat, a rash or hives need emergency services straight away.",
};
/**
 * A dose the pen in her hand cannot produce.
 *
 * The restricted lists are the point: a patient whose dose rises mid-cycle can
 * be holding a pen that does not reach it, and the documented error is doing the
 * arithmetic herself across two pens. `clinic-soon` because it is a question for
 * whoever set the dose, and it is asked before she injects rather than after.
 */
const DOSE_THIS_PEN_CANNOT_DELIVER: ProtocolWarning = {
  id: "bemfola.dose-this-pen-cannot-deliver",
  severity: "stop",
  escalation: "clinic-soon",
  copy: {
    en: "A dose this pen cannot deliver - Each pen strength delivers a fixed list of doses. If your prescribed dose is not on the list for the pen in your hand, do not improvise or split it across two pens without advice - contact your clinic.",
    fr: "Une dose que ce stylo ne peut pas délivrer - Chaque dosage de stylo délivre une liste fixe de doses. Si votre dose prescrite n'y figure pas pour le stylo que vous tenez, n'improvisez pas et ne la répartissez pas sur deux stylos sans avis - contactez votre centre.",
  },
  sourceSegment:
    "⚠ A dose this pen cannot deliver - Each pen strength delivers a fixed list of doses. If your prescribed dose is not on the list for the pen in your hand, do not improvise or split it across two pens without advice - contact your clinic.",
};
/** Everything that stops the walk, in the order she would meet it. */
const AT_THE_NEEDLE: ProtocolWarning[] = [
  WHEN_TO_CALL,
  BREATHING_OR_ALLERGIC_REACTION,
];

const STEPS: ProtocolStep[] = [
  {
    // A new pen every evening, said first, because the box holds several
    // and the habit a multi-dose pen teaches is the wrong one here.
    id: "bemfola.pen.prepare",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Prepare - Wash hands and check the expiry date on the pen. Take it out of the refrigerator and let it reach room temperature for 5 to 10 minutes before you inject - do not use a microwave or any other heating element to warm it. Each pen is used once and then thrown away, so take a new one from your pack for every injection.",
      fr: "Préparer - Lavez-vous les mains et vérifiez la date de péremption du stylo. Sortez-le du réfrigérateur et laissez-le revenir à température ambiante 5 à 10 minutes avant d'injecter - n'utilisez ni micro-ondes ni aucun autre moyen de chauffage. Chaque stylo sert une seule fois puis se jette : prenez-en un neuf dans votre boîte à chaque injection.",
    },
    warnings: [MISSED_DOSE],
  },
  {
    id: "bemfola.pen.ready-the-pen",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Attach the needle - Attach one of the needles supplied with the pen and remove both caps. Use only the needles supplied.",
      fr: "Fixer l'aiguille - Fixez une des aiguilles fournies avec le stylo et retirez les deux capuchons. N'utilisez que les aiguilles fournies.",
    },
  },
  {
    // The step a single-use pen needs *more* than a multi-dose one: Gonal-F is
    // primed once and then used for 28 days, so a walk inherited from it primes
    // a patient's pen on day one and never again. Bemfola is a new pen every
    // evening, so skipping this shorts every dose she gives.
    id: "bemfola.pen.prime",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Prime the pen - Hold the pen with the needle pointing upwards and tap the side gently so any air bubbles rise to the top. Still holding it upright, push the dosage knob in until the activation bar with the small arrow disappears. You should hear a click, and some liquid will splash out - this is normal. If no liquid splashes out, or liquid leaks where the needle meets the pen, do not use that pen: start again with a new one and tell your clinic.",
      fr: "Amorcer le stylo - Tenez le stylo aiguille vers le haut et tapotez doucement le côté pour faire remonter les bulles d'air. Toujours à la verticale, enfoncez le sélecteur jusqu'à ce que la barre d'activation avec la petite flèche disparaisse. Vous devez entendre un clic et un peu de liquide doit gicler - c'est normal. Si aucun liquide ne gicle, ou si du liquide fuit à la jonction de l'aiguille et du stylo, n'utilisez pas ce stylo : recommencez avec un neuf et prévenez votre centre.",
    },
  },
  {
    // A `verify` and a confirmation: this pen's strength decides which
    // doses exist at all, and the number in the window is the only place
    // that becomes visible.
    id: "bemfola.pen.set-the-dose",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Set your dose - Dial your prescribed dose. Each strength of pen delivers only certain doses, printed on the carton, so check that the number in the window is the one your clinic prescribed. If your dose is not one this pen can deliver, contact your clinic before injecting.",
      fr: "Régler la dose - Sélectionnez votre dose prescrite. Chaque dosage de stylo ne délivre que certaines doses, imprimées sur la boîte : vérifiez que le chiffre affiché est bien celui que votre centre a prescrit. Si votre dose ne fait pas partie de celles que ce stylo peut délivrer, contactez votre centre avant d'injecter.",
    },
    warnings: [DOSE_THIS_PEN_CANNOT_DELIVER],
  },
  {
    id: "bemfola.pen.clean-site",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Clean the injection site - Swab the lower abdomen with an alcohol swab and let it air-dry. Choose a different injection site each day.",
      fr: "Désinfecter le site - Désinfectez le bas du ventre avec une compresse alcoolisée et laissez sécher. Choisissez un site différent chaque jour.",
    },
  },
  {
    id: "bemfola.pen.inject",
    kind: "irreversible",
    requiresConfirmation: true,
    copy: {
      en: "Inject - Pinch the skin firmly, insert the needle at a 90° angle, and press the dose button slowly and steadily. Keep the needle in place for 5 seconds after the dose button stops moving, then withdraw the needle.",
      fr: "Injecter - Pincez fermement la peau, insérez l'aiguille à 90° et appuyez sur le bouton d'injection lentement et régulièrement. Maintenez l'aiguille en place 5 secondes après l'arrêt du bouton, puis retirez-la.",
    },
    warnings: AT_THE_NEEDLE,
  },
  {
    id: "bemfola.pen.aftercare",
    kind: "aftercare",
    requiresConfirmation: false,
    copy: {
      en: "After injection - Apply gentle pressure with gauze - do not rub. Remove the needle and dispose of the whole pen and the needle in a sharps container. The pen is for one injection only - never keep it for a second dose.",
      fr: "Après l'injection - Appliquez une légère pression avec une compresse - sans frotter. Retirez l'aiguille et jetez le stylo entier et l'aiguille dans un collecteur. Le stylo ne sert qu'à une seule injection - ne le gardez jamais pour une seconde dose.",
    },
  },
];

export const BEMFOLA_PROTOCOL: InjectionProtocol = {
  medicationSlug: "bemfola",
  brands: BRANDS,
  aliases: [],
  // Its leaflet names the front of the thigh beside the abdomen, so the walk
  // asks before she cleans anything and the camera then does whatever it
  // honestly can at the site she named — the same shape ganirelix has.
  sites: [
    {
      id: "abdomen",
      label: { en: "Lower abdomen", fr: "Bas du ventre" },
      copy: {
        en: "Clean the injection site - Swab the lower abdomen with an alcohol swab and let it air-dry. Choose a different injection site each day.",
        fr: "Désinfecter le site - Désinfectez le bas du ventre avec une compresse alcoolisée et laissez sécher. Choisissez un site différent chaque jour.",
      },
    },
    {
      id: "thigh",
      label: { en: "Front of the thigh", fr: "Face avant de la cuisse" },
      copy: {
        en: "If you use your thigh instead - Swab the front of your thigh with an alcohol swab and let it air-dry. Choose a different injection site each day.",
        fr: "Si vous utilisez la cuisse - Désinfectez la face avant de la cuisse avec une compresse alcoolisée et laissez sécher. Choisissez un site différent chaque jour.",
      },
    },
  ],
  indication: "stimulation",
  // In IU, as the approved content doses it ("75-450 IU daily"), and titrated:
  // her clinic sets the number and changes it mid-course, so there are no
  // strengths to check it against. Only the unit can be wrong on a record.
  dose: { unit: "IU" },
  form: "pen",
  prescriptionForms: ["pre_filled_pen"],
  route: "subcutaneous",
  markets: [
    "AT",
    "BE",
    "BG",
    "CY",
    "CZ",
    "DE",
    "DK",
    "EE",
    "ES",
    "FI",
    "FR",
    "GB",
    "GR",
    "HR",
    "HU",
    "IE",
    "IS",
    "IT",
    "LI",
    "LT",
    "LU",
    "LV",
    "MT",
    "NL",
    "NO",
    "PL",
    "PT",
    "RO",
    "SE",
    "SI",
    "SK",
    "AU",
    "NZ",
  ],
  version: "2026-09-28.1",
  derivedFrom: "content/data/medications/gonadotropins.json#bemfola",
  steps: STEPS,
};
